// packages/eden-form-parts/src/utils/sharedCss.js
import { css } from "styled-components";
import {
  accentColorPrimary,
  accentColorDanger,
  accentColorConfirmation,
  accentColorWarning,
  backgroundColorLevelCurrent,
  black,
  grey15,
  textColorTertiary,
  borderColorPrimary,
  borderColorTertiary,
  spacing4,
  spacing8,
  spacing24,
  spacing32,
  spacing64,
  fontSize16,
  textColorPrimary,
  lineHeight,
  sans,
  borderRadius,
  getAppearanceConstant,
  setAppearanceConstantLocal,
  whiteTransparency30,
  whiteTransparency70,
  grey35,
  colorScheme
} from "@churchofjesuschrist/eden-style-constants";
import {
  toHTMLString,
  alertFilled,
  alertWarningFilled,
  circleSelectedFilled
} from "@churchofjesuschrist/eden-icons-raw";
import { dir } from "@churchofjesuschrist/eden-styled-helpers";
import { adjustHexAlpha } from "@churchofjesuschrist/eden-utilities";
var [formElementDisabledBackgroundDef, formElementDisabledBackground] = setAppearanceConstantLocal("form-element-disabled-background", {
  light: adjustHexAlpha(black, 0.12),
  dark: whiteTransparency70,
  prefix: "eden"
});
var [formElementDisabledBorderDef, formElementDisabledBorder] = setAppearanceConstantLocal("form-element-disabled-border", {
  light: grey15,
  dark: borderColorTertiary,
  prefix: "eden"
});
var [
  formElementDisabledSelectedBackgroundDef,
  formElementDisabledSelectedBackground
] = setAppearanceConstantLocal("form-element-disabled-selected-background", {
  light: adjustHexAlpha(black, 0.38),
  dark: whiteTransparency30,
  prefix: "eden"
});
var formElementDisabledColor = textColorTertiary;
var darkModeURLIcon = (icon, { light, dark }, curAppearanceContext, useSafariFallback = false) => {
  let styleBlock = `path{ fill: ${curAppearanceContext?.mode === "dark" ? dark : light};}`;
  styleBlock += curAppearanceContext?.mode === "os" ? `@media (prefers-color-scheme: dark) {
        path{fill: ${dark};}
}
${useSafariFallback ? `@supports (-webkit-hyphens:none) { path{fill: ${grey35};}` : ""}
` : ``;
  return `url('data:image/svg+xml; utf8, ${encodeURIComponent(
    icon.replace(
      // find the close of the opening tag and the start of the next tag
      />\s*</,
      // inject the following style block just after the opening tag in the SVG
      `><style>${styleBlock.replace("  ", "")}</style><`
    )
  ).replaceAll("(", "%28").replaceAll(")", "%29")}')`;
};
var errorBackground = toHTMLString({
  ...alertFilled,
  props: {
    ...alertFilled.props,
    style: `width: ${spacing24}, height: ${spacing24}`
  }
});
var warningBackground = toHTMLString({
  ...alertWarningFilled,
  props: {
    ...alertWarningFilled.props,
    style: `width: ${spacing24}, height: ${spacing24}`
  }
});
var successBackground = toHTMLString({
  ...circleSelectedFilled,
  props: {
    ...circleSelectedFilled.props,
    style: `width: ${spacing24}, height: ${spacing24}`
  }
});
var valuesByValidityState = {
  error: {
    borderColor: accentColorDanger,
    background: errorBackground,
    modeStyle: getAppearanceConstant(accentColorDanger)
  },
  warning: {
    borderColor: borderColorPrimary,
    background: warningBackground,
    modeStyle: getAppearanceConstant(accentColorWarning)
  },
  success: {
    borderColor: borderColorPrimary,
    background: successBackground,
    modeStyle: getAppearanceConstant(accentColorConfirmation)
  }
};
var internalUseOnlyOpenDetailsStyles = ({
  $icon,
  $curAppearanceContext,
  $validityState
}) => {
  const { background, modeStyle } = valuesByValidityState[$validityState] || {};
  return css`
    background-image: ${background ? `${darkModeURLIcon(background, modeStyle, $curAppearanceContext)},` : ""}
      ${darkModeURLIcon(
    $icon,
    getAppearanceConstant(textColorPrimary),
    $curAppearanceContext,
    true
  )};
    &:disabled {
      background-image: ${background ? `${darkModeURLIcon(background, modeStyle, $curAppearanceContext)},` : ""}
        ${darkModeURLIcon(
    $icon,
    getAppearanceConstant(formElementDisabledColor),
    $curAppearanceContext
  )};
    }
    padding-inline-end: ${background ? spacing64 : spacing32};
    background-position: ${background ? `right ${spacing32} center,` : ""} right
      ${spacing8} center;
    background-size: ${background ? "1.5rem 1.5rem," : ""} 1.5rem 1.5rem;

    ${dir("rtl")`
      background-position: ${background ? `left ${spacing32} center,` : ""} left ${spacing8} center;

      /* Switches the chrome injected date and time placeholder text */
      &::-webkit-datetime-edit {
        text-align: end;
      }
    `}
  `;
};
var sharedCheckCss = css`
  line-height: ${lineHeight};
  color: inherit;
  margin: 0;
  padding: 0;
  appearance: none;
  border: 1px solid ${borderColorPrimary};
  min-height: 1.125rem;
  min-width: 1.125rem;
  max-width: 1.125rem;
  max-height: 1.125rem;
  background-color: ${backgroundColorLevelCurrent};
  background-repeat: no-repeat, no-repeat;
  background-position:
    50% 50%,
    top left;

  ${formElementDisabledBackgroundDef}
  ${formElementDisabledBorderDef}
  ${formElementDisabledSelectedBackgroundDef}
  &:disabled {
    border-color: ${formElementDisabledBorder};
    background-color: ${formElementDisabledBackground};
    cursor: not-allowed;
  }

  &:checked,
  &[type="checkbox"]:indeterminate {
    background-color: ${accentColorPrimary};
    border-color: ${accentColorPrimary};

    &:disabled {
      border-color: ${formElementDisabledBorder};
      background-color: ${formElementDisabledSelectedBackground};
    }
  }
`;
var sharedInputCss = css`
  color: ${textColorPrimary};
  background-color: ${backgroundColorLevelCurrent};
  color-scheme: ${colorScheme};
  border-radius: ${borderRadius};
  font-size: ${fontSize16};
  font-family: ${sans};
  line-height: ${lineHeight};
  padding: ${spacing8};
  width: 100%;
  min-width: 0;

  background-repeat: no-repeat;
  background-size: 1.5rem 1.5rem;

  ${({ $validityState, $small, $curAppearanceContext }) => {
  const { borderColor, background, modeStyle } = valuesByValidityState[$validityState] || {};
  return css`
      background-image: ${background ? darkModeURLIcon(background, modeStyle, $curAppearanceContext) : ""};
      padding-inline-end: ${background ? "2.5rem" : spacing8};
      border: 1px solid ${borderColor || borderColorPrimary};
      background-position: right ${$small ? spacing4 : spacing8} top
        ${$small ? spacing4 : spacing8};
      ${dir("rtl")`
        background-position: left ${$small ? spacing4 : spacing8} top
          ${$small ? spacing4 : spacing8}
      `}
      height: ${$small ? spacing32 : "2.5rem"};
    `;
}};

  &::placeholder {
    color: ${textColorTertiary};
  }

  ${formElementDisabledBackgroundDef}
  ${formElementDisabledBorderDef}
  &:disabled {
    border-color: ${formElementDisabledBorder};
    background-color: ${formElementDisabledBackground};
    color: ${formElementDisabledColor};
    resize: none;
    cursor: not-allowed;
  }

  &:not(select):not(:disabled):read-only {
    border: none;
    resize: none;
  }
`;
export {
  darkModeURLIcon,
  formElementDisabledBackground,
  formElementDisabledBackgroundDef,
  formElementDisabledBorder,
  formElementDisabledBorderDef,
  internalUseOnlyOpenDetailsStyles,
  sharedCheckCss,
  sharedInputCss,
  valuesByValidityState
};
